import { Icon, IconButton, Wizard } from '@lounge-fe/ui-kit'

import { ShopifySectionComponent } from '@/types/ShopifySectionComponent'

import { useExponea } from '../../../hooks/useExponea'
import { useShopify } from '../../../hooks/useShopify'
import { Progress } from '../Progress'
import {
  Welcome,
  CurrentBrand,
  BraSize,
  BraStyle,
  BraAge,
  CupFit,
  HookFit,
  YourLoungeFit,
  Confirmation,
  BottomsSize,
  StrapFit,
  BandFit,
} from './Steps'

import {
  FittingRoomProvider,
  useFittingRoomContext,
} from './FittingRoomProvider'
import { StoreStateSync } from './StoreStateSync'
import { EmailCollection } from './Steps/EmailCollection'
import type { FittingRoomFormData, FittingRoomValues } from './types'
import { useTranslation } from 'react-i18next'

const initialValues: FittingRoomValues = {
  currentBrand: null,
  bandSize: null,
  cupSize: null,
  preference: null,
  braAge: null,
  cupFit: null,
  bandFit: null,
  hookFit: null,
  strapFit: null,
  bottomsSize: null,
  email: '',
  acceptsMarketing: false,
  loungeFit: null,
}

export const WrappedForm = () => {
  const shopify = useShopify()
  const { t } = useTranslation()
  const { track, consent, identify, update } = useExponea()
  const { returnTo } = useFittingRoomContext()

  const onSubmit = (values: FittingRoomValues) => {
    identify(values.email.toLowerCase().trim())

    if (values.acceptsMarketing) {
      consent('email', true)
      consent('sms', true)
    }

    update({
      domain: shopify.shop,
    })

    const event: Record<string, any> = {
      store: shopify.shop,
      valid: values.loungeFit?.inStock,
      date: new Date(Date.now()).toISOString(),
      bra_style: values.preference,
      go_to: Boolean(values.currentBrand),
      current_brand_other: values.currentBrand,
      entered_band_size: values.bandSize,
      entered_cup_size: values.cupSize,
      bra_age: values.braAge,
      band_fit: values.bandFit,
      hook: values.hookFit,
      cup_fit: values.hookFit,
      strap_fit: values.strapFit,
      underwear_size: values.bottomsSize,
      email: values.email,
      agree: values.acceptsMarketing,
    }

    if (values.loungeFit?.bandSize && values.loungeFit?.cupSize) {
      event.band_size = values.loungeFit.bandSize
      event.cup_size = values.loungeFit.cupSize
      event.bra_size = `${values.loungeFit.bandSize}${values.loungeFit.cupSize}`
      event.bralet_size = values.loungeFit.braletteSize
    }

    track('the-fitting-room', event)
  }

  return (
    <Wizard initialValues={initialValues} onSubmit={onSubmit}>
      {({ isSubmitSuccessful }) => (
        <>
          <StoreStateSync />
          <IconButton
            aria-label={t('action.close')}
            icon={<Icon.Close />}
            as="a"
            href={returnTo}
            className="absolute top-8 right-8 bg-[#F5F5F5]"
          />

          <div className="h-full md:h-screen flex flex-col items-center sm:py-0r">
            <div className="w-full flex flex-col justify-center flex-1 sm:w-[530px] max-w-full gap-y-4 px-6 py-10">
              {isSubmitSuccessful ? (
                <YourLoungeFit />
              ) : (
                <>
                  <Wizard.Steps>
                    <Welcome />
                    <CurrentBrand />
                    <BraSize />
                    <BraStyle />
                    <BraAge />
                    <CupFit />
                    <BandFit />
                    <HookFit />
                    <StrapFit />
                    <BottomsSize />
                    <Confirmation />
                    <EmailCollection />
                  </Wizard.Steps>
                </>
              )}
            </div>
            <Progress isComplete={isSubmitSuccessful} />
          </div>
        </>
      )}
    </Wizard>
  )
}

export const Form: ShopifySectionComponent<FittingRoomFormData> = ({
  data,
  ...props
}) => {
  if (!data) {
    return <></>
  }

  return (
    <FittingRoomProvider data={data}>
      <WrappedForm {...props} />
    </FittingRoomProvider>
  )
}
